import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Copy, Loader2 } from "lucide-react";
import { PageHeader, Stat, Pill, Table } from "./ui";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminReferrals() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/referrals", { params: filter ? { status: filter } : {} });
      setData(r.data);
    } catch { setData({ referrals: [], counts: { PENDING: 0, APPROVED: 0, REJECTED: 0, total: 0 } }); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post(`/admin/referrals/${approveTarget.referral_id}/approve`);
      setResult({ ...r.data, client: approveTarget.client_name });
      setApproveTarget(null);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/admin/referrals/${rejectTarget.referral_id}/reject`, { reason });
      setRejectTarget(null); setReason("");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const counts = data?.counts || {};
  const columns = [
    { label: "Client", render: (r) => (<div><p className="font-medium text-[#0B1B33]">{r.client_name}</p><p className="text-[11px] text-[#7C8AA3]">{r.client_email}{r.client_phone ? ` • ${r.client_phone}` : ""}</p></div>) },
    { label: "Company", key: "company" },
    { label: "Plan", key: "plan_interested" },
    { label: "Partner", render: (r) => (<div><p className="text-[#22355A]">{r.partner_name}</p><p className="text-[11px] text-[#93A3BC] font-mono">{r.partner_id}</p></div>) },
    { label: "Date", render: (r) => new Date(r.created_at).toLocaleDateString() },
    { label: "Status", render: (r) => (<div><Pill value={r.status} />{r.status === "REJECTED" && r.admin_note && <p className="text-[10px] text-red-600 mt-1 max-w-[180px]">{r.admin_note}</p>}</div>) },
    { label: "Actions", render: (r) => r.status === "PENDING" ? (
      <div className="flex gap-1.5">
        <button onClick={() => { setApproveTarget(r); setError(""); }} data-testid={`approve-${r.referral_id}`} className="adm-btn-primary !px-2.5 !py-1 text-[10px]"><CheckCircle2 size={11} /> APPROVE</button>
        <button onClick={() => { setRejectTarget(r); setReason(""); setError(""); }} data-testid={`reject-${r.referral_id}`} className="adm-btn-ghost !px-2.5 !py-1 text-[10px] !text-red-600 !border-red-200"><XCircle size={11} /> REJECT</button>
      </div>
    ) : <span className="text-[11px] text-[#93A3BC]">—</span> },
  ];

  return (
    <div data-testid="admin-referrals-page">
      <PageHeader kicker="Partner Workflow" title="Referral Approvals"
        sub="Partner-submitted client referrals. Approving sends the client a membership invitation email with a secure payment link." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="referral-stats">
        <Stat label="Pending Review" value={counts.PENDING ?? 0} testid="stat-pending" />
        <Stat label="Approved" value={counts.APPROVED ?? 0} testid="stat-approved" />
        <Stat label="Rejected" value={counts.REJECTED ?? 0} testid="stat-rejected" />
        <Stat label="Total Referrals" value={counts.total ?? 0} testid="stat-total" />
      </div>

      <div className="flex gap-2 mb-4" data-testid="referral-filters">
        {[["", "ALL"], ["PENDING", "PENDING"], ["APPROVED", "APPROVED"], ["REJECTED", "REJECTED"]].map(([v, label]) => (
          <button key={label} onClick={() => setFilter(v)} data-testid={`filter-${label.toLowerCase()}`}
            className={`adm-pill cursor-pointer transition-colors ${filter === v ? "!border-[#2563EB] !text-[#2563EB] !bg-blue-50" : ""}`}>{label}</button>
        ))}
      </div>

      {result && (
        <div className="adm-card p-4 mb-4 border-emerald-300 bg-emerald-50/60 flex flex-wrap items-center gap-3" data-testid="approve-result">
          <CheckCircle2 size={16} className="text-emerald-600" />
          <p className="text-[13px] text-emerald-800 flex-1">
            {result.client} approved — invitation email {result.invitation === "sent" ? "sent" : "FAILED to send (check Email Infrastructure)"}.
          </p>
          {result.payment_link && (
            <>
              <code className="text-[11px] text-[#2563EB]" data-testid="result-payment-link">{window.location.origin}{result.payment_link}</code>
              <button onClick={() => { navigator.clipboard.writeText(window.location.origin + result.payment_link).catch(() => {}); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                data-testid="copy-payment-link-btn" className="adm-btn-ghost !px-2.5 !py-1 text-[10px]">{copied ? "COPIED" : <><Copy size={11} /> COPY</>}</button>
            </>
          )}
          <button onClick={() => setResult(null)} data-testid="dismiss-result-btn" className="text-[#7C8AA3] text-xs">✕</button>
        </div>
      )}

      <Table columns={columns} rows={data?.referrals} testid="referrals-table" empty="No partner referrals yet." />

      {approveTarget && (
        <div className="fixed inset-0 z-50 bg-[#0B1B33]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setApproveTarget(null)} data-testid="approve-modal">
          <div className="adm-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#0B1B33] mb-2">Approve Referral</h3>
            <p className="text-[13px] text-[#5B6B87] mb-1"><b>{approveTarget.client_name}</b> ({approveTarget.client_email}) — {approveTarget.company}</p>
            <p className="text-[12px] text-[#7C8AA3] mb-4">Plan: {approveTarget.plan_interested}. The client will receive an invitation email with a payment link.</p>
            {error && <p className="text-xs text-red-600 mb-3" data-testid="approve-error">{error}</p>}
            <div className="flex gap-2">
              <button onClick={approve} disabled={busy} data-testid="approve-confirm-btn" className="adm-btn-primary flex-1 disabled:opacity-60">
                {busy ? <Loader2 size={14} className="animate-spin" /> : "APPROVE & SEND INVITATION"}
              </button>
              <button onClick={() => setApproveTarget(null)} data-testid="approve-cancel-btn" className="adm-btn-ghost">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 bg-[#0B1B33]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setRejectTarget(null)} data-testid="reject-modal">
          <div className="adm-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#0B1B33] mb-2">Reject Referral</h3>
            <p className="text-[13px] text-[#5B6B87] mb-3"><b>{rejectTarget.client_name}</b> — {rejectTarget.company}. The partner can update and resubmit.</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (required, min 5 characters)"
              data-testid="reject-reason-input" className="w-full border border-[#E4EAF3] rounded-md px-3 py-2 text-[13px] mb-3 resize-none focus:outline-none focus:border-[#2563EB]" />
            {error && <p className="text-xs text-red-600 mb-3" data-testid="reject-error">{error}</p>}
            <div className="flex gap-2">
              <button onClick={reject} disabled={busy || reason.trim().length < 5} data-testid="reject-confirm-btn" className="adm-btn-primary flex-1 disabled:opacity-60">
                {busy ? "SAVING…" : "REJECT REFERRAL"}
              </button>
              <button onClick={() => setRejectTarget(null)} data-testid="reject-cancel-btn" className="adm-btn-ghost">CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
